import {
  Box,
  ClickAwayListener,
  InputAdornment,
  ListItemText,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  TextField,
  FormHelperText,
  FormControl,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import CheckIcon from "@mui/icons-material/Check";
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { styled } from '@mui/material/styles';

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(1),
  '& .MuiListItemText-root': {
    flexGrow: 1,
  },
}));

const MuiNestedSubDropDown = ({ label, options = [], onChange, required, defaultValue }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [hoverPath, setHoverPath] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [inputWidth, setInputWidth] = useState(0);

  // New state to track if the dropdown has been opened by user click
  const [hasOpened, setHasOpened] = useState(false);
  // New state to track if an item has been selected from the dropdown
  const [hasSelected, setHasSelected] = useState(false);


  const inputRef = useRef(null);
  const timeoutRef = useRef(null);
  const menuItemRefs = useRef(new Map());

  // Utility to normalize values consistently
  const normalize = (value) => {
    if (value === undefined || value === null || value === '') {
      return null;
    }
    return value;
  };

  useEffect(() => {
    if (inputRef.current) {
      setInputWidth(inputRef.current.offsetWidth);
    }
  }, [anchorEl]);

  // Handle defaultValue initialization
  useEffect(() => {
    const normalizedDefault = defaultValue
      ? (
        normalize(defaultValue.element) !== null &&
        normalize(defaultValue.subElement) !== null &&
        normalize(defaultValue.leaf) === null
      )
        ? {
          element: normalize(defaultValue.element),
          subElement: null,
          leaf: normalize(defaultValue.subElement),
        }
        : {
          element: normalize(defaultValue.element),
          subElement: normalize(defaultValue.subElement),
          leaf: normalize(defaultValue.leaf),
        }
      : null;
    setSelectedItem(normalizedDefault);

    // If a defaultValue is provided, it means the field is pre-filled and valid.
    // So, we consider it both 'opened' (implicitly handled) and 'selected'.
    if (normalizedDefault && (normalizedDefault.element || normalizedDefault.subElement || normalizedDefault.leaf)) {
      setHasOpened(true);
      setHasSelected(true);
    } else {
      // If no defaultValue, or it's empty, reset both flags.
      setHasOpened(false);
      setHasSelected(false);
    }
  }, [defaultValue]);

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
    // User explicitly opened the dropdown
    setHasOpened(true);
  };

  const handleClose = useCallback(() => {
    setAnchorEl(null);
    setHoverPath([]);
    clearTimeout(timeoutRef.current);
    menuItemRefs.current.clear();
  }, []);

  const handleSelect = (item) => {
    const normalizedItem = {
      element: normalize(item.element),
      subElement: normalize(item.subElement),
      leaf: normalize(item.leaf),
    };
    setSelectedItem(normalizedItem);
    if (onChange) onChange(normalizedItem);
    // User has made a selection
    setHasSelected(true);
    handleClose();
  };

  const isSelected = (item) => {
    if (!selectedItem) return false;

    const currentElement = normalize(item.element);
    const currentSubElement = normalize(item.subElement);
    const currentLeaf = normalize(item.leaf);

    const selectedElement = normalize(selectedItem.element);
    const selectedSubElement = normalize(selectedItem.subElement);
    const selectedLeaf = normalize(selectedItem.leaf);

    const matchElement = currentElement === selectedElement;
    const matchSubElement =
      currentSubElement === selectedSubElement ||
      (!currentSubElement && !selectedSubElement);
    const matchLeaf =
      selectedLeaf === null || currentLeaf === selectedLeaf;

    const result = matchElement && matchSubElement && matchLeaf;
    return result;
  };

  const startCloseTimeout = (level) => {
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      setHoverPath((prev) => prev.slice(0, level));
    }, 100);
  };

  const handleHover = (index, level, event) => {
    clearTimeout(timeoutRef.current);
    const key = `${level}-${index}`;
    menuItemRefs.current.set(key, event.currentTarget);
    setHoverPath((prev) => {
      if (prev[level] !== index) {
        return [...prev.slice(0, level), index];
      }
      return prev;
    });
  };

  const getSelectedText = () => {
    if (!selectedItem) return "";
    const { element, subElement, leaf } = selectedItem;

    const displayElement = normalize(element);
    const displaySubElement = normalize(subElement);
    const displayLeaf = normalize(leaf);

    if (!displayElement) return "";

    if (displayElement && displaySubElement && displayLeaf) {
      if (displayElement === displaySubElement) {
        return `${displayElement}: ${displayLeaf}`;
      }
      return `${displayElement}: ${displaySubElement}: ${displayLeaf}`;
    }

    if (displayElement && displayLeaf) {
      return `${displayElement}: ${displayLeaf}`;
    }

    if (displayElement && displaySubElement && !displayLeaf) {
      return `${displayElement}: ${displaySubElement}`;
    }

    if (displayElement && !displaySubElement && !displayLeaf) {
      return displayElement;
    }

    return "";
  };

  const displayErrorMessage = () => {
    return required && hasOpened && !hasSelected && !Boolean(anchorEl);
  };

  const renderMenu = (
    items,
    level = 0,
    anchor = null,
    parentElement = null,
    parentSubElement = null
  ) => {
    if (!items || items.length === 0) return null;

    const openPopper = level === 0 ? Boolean(anchorEl) : hoverPath.length >= level;
    const parentIndex = hoverPath[level - 1];
    const currentKey = `${level === 0 ? 0 : level - 1}-${parentIndex}`;
    const currentAnchor = level === 0 ? anchorEl : menuItemRefs.current.get(currentKey);

    return (
      <Popper
        open={openPopper}
        anchorEl={currentAnchor}
        placement={level === 0 ? "bottom-start" : "right-start"}
        style={{
          zIndex: 1300,
          minWidth: level === 0 ? `${inputWidth - 160}px` : "200px",
        }}
      >
        <Paper>
          <MenuList>
            {items.map((item, index) => {
              const isHovered = hoverPath[level] === index;

              const hasSubElements = item.elements?.length > 0;
              const hasLeaves = item.leaves?.length > 0;
              const hasChildren = hasSubElements || hasLeaves;

              const displayText = item.leaf || item.element;

              let subItems = [];
              let nextParentElement = parentElement;
              let nextParentSubElement = parentSubElement;
              let currentItemForComparisonAndSelection = {};

              if (
                item.hasOwnProperty("element") &&
                item.hasOwnProperty("leaf") &&
                !item.hasOwnProperty("elements") &&
                !item.hasOwnProperty("leaves")
              ) {
                currentItemForComparisonAndSelection = {
                  element: item.element,
                  subElement: item.subElement || null,
                  leaf: item.leaf,
                };
              } else if (item.leaf) {
                const topLevel = parentElement || item.element;
                const subLevel = parentSubElement || null;
                currentItemForComparisonAndSelection = {
                  element: topLevel,
                  subElement: subLevel,
                  leaf: item.leaf,
                };
              } else if (item.element && !item.leaves && !item.elements) {
                currentItemForComparisonAndSelection = {
                  element: item.element,
                  subElement: null,
                  leaf: null,
                };
              } else {
                const topLevel = parentElement || options[hoverPath[0]]?.element || null;
                currentItemForComparisonAndSelection = {
                  element: topLevel,
                  subElement: item.element,
                  leaf: null,
                };
              }

              if (hasSubElements) {
                subItems = item.elements;
                nextParentElement = level === 0 ? item.element : (parentElement || options[hoverPath[0]]?.element);
                nextParentSubElement = item.element;
              } else if (hasLeaves) {
                subItems = item.leaves.map((leaf) => {
                  const formatted = {
                    element: parentElement || item.element,
                    subElement: parentElement ? item.element : null,
                    leaf,
                  };
                  return formatted;
                });

                nextParentElement = level === 0 ? item.element : (parentElement || options[hoverPath[0]]?.element);
                nextParentSubElement = item.element;
              }

              const onItemClick = (e) => {
                e.stopPropagation();
                if (!hasChildren) {
                  handleSelect(currentItemForComparisonAndSelection);
                }
              };

              const key = `${level}-${index}`;

              return (
                <StyledMenuItem
                  key={key}
                  onMouseEnter={(e) => handleHover(index, level, e)}
                  onClick={onItemClick}
                  ref={(el) => {
                    if (el) {
                      menuItemRefs.current.set(key, el);
                    }
                  }}
                  sx={{
                    backgroundColor: isSelected(currentItemForComparisonAndSelection) ? 'rgba(0, 105, 109, 0.08)' : 'inherit'
                  }}
                >
                  <ListItemText primary={displayText} />
                  <Box sx={{ display: 'flex', alignItems: 'center', marginLeft: 'auto', gap: 1 }}>
                    {isSelected(currentItemForComparisonAndSelection) && (
                      <CheckIcon
                        fontSize="small"
                        sx={{
                          color: '#00696D',
                          visibility: 'visible'
                        }}
                      />
                    )}
                    {hasChildren && <ArrowRightIcon fontSize="small" />}
                  </Box>
                  {isHovered && hasChildren && currentAnchor &&
                    renderMenu(
                      subItems,
                      level + 1,
                      currentAnchor,
                      nextParentElement,
                      nextParentSubElement
                    )}
                </StyledMenuItem>
              );
            })}
          </MenuList>
        </Paper>
      </Popper>
    );
  };

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <Box sx={{ width: "100%" }}>
        <FormControl fullWidth error={displayErrorMessage()}>
          <TextField
            inputRef={inputRef}
            label={label}
            fullWidth
            value={getSelectedText()}
            onClick={handleOpen}
            InputProps={{
              readOnly: true,
              style: { cursor: "pointer" },
              endAdornment: (
                <InputAdornment position="end">
                  <ArrowDropDownIcon />
                </InputAdornment>
              ),
            }}
            sx={{
              "& label.Mui-focused": {
                color: "#00696D",
              },
              "& .MuiInput-underline:after": {
                borderBottomColor: "#00696D",
              },
              "& .MuiOutlinedInput-root": {
                "&.Mui-focused fieldset": {
                  borderColor: "#00696D",
                },
              },
            }}
          />
          {displayErrorMessage() && (
            <FormHelperText>{'Dette feltet er påkrevd'}</FormHelperText>
          )}
        </FormControl>
        {renderMenu(options)}
      </Box>
    </ClickAwayListener>
  );
};

export default MuiNestedSubDropDown;